import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Hercles Global';
const BASE_URL = (process.env.REACT_APP_SITE_URL || 'https://herclesglobal.com').replace(/\/+$/, '');
const DEFAULT_OG_IMAGE = `${BASE_URL}/hercles-social-card.png`;
const DEFAULT_ROBOTS = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

/**
 * SEOMeta — drop this inside any page component to set dynamic <head> tags.
 *
 * Props:
 *   title          — page <title> (appended with " | Hercles Global" unless noSuffix)
 *   description    — meta description (≤160 chars ideal)
 *   canonical      — canonical URL path, e.g. "/product/42" (BASE_URL prepended automatically)
 *   ogImage        — absolute URL for og:image (defaults to og-image.jpg)
 *   noSuffix       — pass true to use title as-is
 *   jsonLd         — plain JS object or array for JSON-LD <script type="application/ld+json">
 */
function buildCanonicalUrl(canonical) {
  if (!canonical) return BASE_URL;
  if (/^https?:\/\//i.test(canonical)) return canonical.replace(/\/+$/, '');
  const path = canonical.startsWith('/') ? canonical : `/${canonical}`;
  return `${BASE_URL}${path === '/' ? '/' : path.replace(/\/+$/, '')}`;
}

function SEOMeta({ title, description, canonical, ogImage, noSuffix = false, robots = DEFAULT_ROBOTS, type = 'website', jsonLd }) {
  const fullTitle = noSuffix ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = buildCanonicalUrl(canonical);
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default SEOMeta;
